import { SearchIcon, Spacer, Title, WarningIcon } from '@kvdbil/components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DefaultSpinner from '@components/ui/DefaultSpinner';
import styled from 'styled-components';
import { useDebounce } from '@hooks/useDebounce';
import { useSelector } from 'react-redux';
import { fetchItemsThunk } from '@data/actions/itemsActions';
import {
  selectAllItems,
  selectItemsIsLoading,
} from '@data/selectors/itemSelectors';
import { useAppDispatch } from '@hooks/reduxHooks';
import { resetItems } from '@data/slices/itemSlice';
import ItemCard from './ItemCard';
import { formatRegistrationPlate, getVehicleFullName } from '@utils/vehicle';
import { Link, useLocation } from 'react-router-dom';
import { vehicleTypeChipSwitch } from './VehicleTypeChipSwitch';

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
`;

const IconContainer = styled.div<{ show: boolean }>`
  display: ${({ show }) => (show ? 'flex' : 'none')};

  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4rem 0;
  color: ${({ theme }) => theme.colors.text.disabled};

  & > svg {
    font-size: 4rem;
  }
`;
const MIN_QUERY_LENGTH = 3;

interface ResultsProps {
  query: string;
}
export const ItemList = ({ query }: ResultsProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const items = useSelector(selectAllItems);
  const debouncedQuery = useDebounce(query, 500);
  const isLoading = useSelector(selectItemsIsLoading);

  useEffect(() => {
    if (debouncedQuery.length >= MIN_QUERY_LENGTH) {
      dispatch(fetchItemsThunk(debouncedQuery));
    }
  }, [debouncedQuery, dispatch]);

  // To stop flickering, reset items when query changes
  useEffect(() => {
    if (query !== debouncedQuery) {
      dispatch(resetItems());
    }
  }, [debouncedQuery, dispatch, query]);

  if (isLoading || query !== debouncedQuery) {
    return (
      <IconContainer show>
        <DefaultSpinner />
      </IconContainer>
    );
  }

  if (query.length < MIN_QUERY_LENGTH) {
    return (
      <IconContainer show>
        <SearchIcon />
        <Spacer height={0.5} />
        <Title>{t('Search Object')}</Title>
      </IconContainer>
    );
  }

  if (items.length === 0) {
    return (
      <IconContainer show>
        <WarningIcon />
        <Spacer height={0.5} />
        <Title>{`${t('No results found for')}: ${query}`}</Title>
      </IconContainer>
    );
  }

  return (
    <ListContainer>
      {items.map(item => {
        const formattedRegNr = formatRegistrationPlate(
          item?.properties?.registrationPlate ?? ''
        );
        const subTitle = getVehicleFullName(item);
        const title = `${item?.properties?.registrationPlate ? formattedRegNr + ' | ' : ''}${item.id}`;
        const chipsData = [vehicleTypeChipSwitch(t, item.vehicleType)];

        return (
          <Link to={`${location.pathname}/${item.id}`} key={item.id}>
            <ItemCard
              itemId={item.id}
              title={title}
              subTitle={subTitle}
              chips={chipsData}
            />
          </Link>
        );
      })}
    </ListContainer>
  );
};
